import type { AdminProductFormValues, AdminProductValidationSection } from '../../interfaces';

type FieldNamePath = Array<string | number>;

type CreateValidationError = {
  errors?: string[];
  name: FieldNamePath;
};

const fieldLabels: Record<string, string> = {
  productTypeId: 'Select a product type.',
  gender: 'Select a gender.',
  title: 'Enter a product title.',
  price: 'Enter a valid base price.',
  description: 'Add a product description.',
  sizeIds: 'Select at least one size.',
  materialName: 'Choose a material.',
  materialIconId: 'Choose a material so its icon can be saved.',
  images: 'Upload at least one material image.',
  stockQuantity: 'Enter material stock.',
  variantStocks: 'Enter stock for every selected size.',
};

const getFieldLabel = (fieldKey: string, fallback?: string) =>
  fieldLabels[fieldKey] || fallback || 'Complete this field.';

const getMaterialIndex = (path: FieldNamePath) => {
  const materialsIndex = path.findIndex((part) => part === 'materials');
  const possibleIndex = materialsIndex >= 0 ? path[materialsIndex + 1] : path[0];

  return typeof possibleIndex === 'number' ? possibleIndex : null;
};

const addSectionItem = (
  sectionsByKey: Map<string, AdminProductValidationSection>,
  sectionKey: string,
  sectionTitle: string,
  item: string
) => {
  const currentSection = sectionsByKey.get(sectionKey) || { key: sectionKey, title: sectionTitle, items: [] };

  if (!currentSection.items.includes(item)) {
    currentSection.items.push(item);
  }

  sectionsByKey.set(sectionKey, currentSection);
};

export const buildAdminProductValidationSummary = (
  errorFields: CreateValidationError[],
  values: Partial<AdminProductFormValues>
) => {
  const sectionsByKey = new Map<string, AdminProductValidationSection>();
  const invalidMaterialIndexes = new Set<number>();

  errorFields.forEach(({ errors = [], name }) => {
    const fieldKey = String(name[name.length - 1]);
    const materialIndex = getMaterialIndex(name);

    if (materialIndex !== null) {
      const materialName = values.materials?.[materialIndex]?.materialName;
      const fieldMessage = fieldKey === 'price'
        ? 'Enter a material price greater than 0.'
        : getFieldLabel(fieldKey, errors[0]);

      invalidMaterialIndexes.add(materialIndex);
      addSectionItem(
        sectionsByKey,
        `material-${materialIndex}`,
        `Material ${materialIndex + 1}${materialName ? `: ${materialName}` : ''}`,
        fieldMessage
      );
      return;
    }

    const fieldMessage = getFieldLabel(fieldKey, errors[0]);

    if (fieldKey === 'productTypeId' || fieldKey === 'gender') {
      addSectionItem(sectionsByKey, 'setup', 'Product setup', fieldMessage);
      return;
    }

    if (fieldKey === 'sizeIds') {
      addSectionItem(sectionsByKey, 'sizes', 'Default size grid', fieldMessage);
      return;
    }

    addSectionItem(sectionsByKey, 'basic', 'Basic information', fieldMessage);
  });

  const detailsValues = [
    values.detailsStyle,
    values.detailsMetal,
    values.detailsWidth,
    values.detailsLength,
    values.detailsSetting,
    values.detailsBacking,
  ];
  const recommendations = detailsValues.some(Boolean)
    ? []
    : ['Product details are recommended for a richer product page, but they do not block creation.'];

  return {
    invalidMaterialIndexes: Array.from(invalidMaterialIndexes),
    recommendations,
    requiredSections: Array.from(sectionsByKey.values()),
  };
};
